import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { FiltroRespuesta } from "./Filtro.respuestas";
import { Nilable } from "@shared/utils";
import { EncuestaDTO, InvestigacionDTO, ItemPreguntaDTO, PreguntaDTO } from "@DTOS";
import { Location } from "@angular/common";

@Injectable({
    providedIn: "root"
})
export class FiltrosService implements OnDestroy {
    public filtros$: Observable<FiltroRespuesta[]>;

    private _filtros$ = new BehaviorSubject<FiltroRespuesta[]>([]);
    private subs = new Subscription();

    constructor(private location: Location) {
        this.filtros$ = this._filtros$.asObservable();
        this.subs.add(
            this.filtros$.subscribe(filtros => {
                if (filtros.length !== 0) {
                    this.guardarFiltrosUrl(filtros);
                } else {
                    this.borrarFiltrosURL();
                }
            })
        );
    }

    public ngOnDestroy() {
        this.subs.unsubscribe();
    }

    public obtenerFiltros(): FiltroRespuesta[] {
        return this._filtros$.value;
    }

    public limpiarFiltros(): void {
        this._filtros$.next([]);
    }

    public obtenerFiltrosSinComparativos(): FiltroRespuesta[] {
        return this.obtenerFiltros().filter(filtro => !filtro.esComparativa);
    }

    public borrarFiltroPorIndice(index: number) {
        const filtros = [...this._filtros$.value];
        filtros.splice(index);
        this._filtros$.next(filtros);
    }

    public agregarFiltro(filtro: FiltroRespuesta) {
        const filtros = filtro.esComparativa ? this.sacarComparativoViejo() : this.obtenerFiltros();
        this._filtros$.next([...filtros, filtro]);
    }

    public agregarFiltros(filtros: FiltroRespuesta[]) {
        const filtrosActuales =
            filtros.some(filtro => filtro.esComparativa) ? this.sacarComparativoViejo() : this.obtenerFiltros();
        this._filtros$.next([...filtrosActuales, ...filtros]);
    }

    public hayFiltroComparativo(): boolean {
        return this.obtenerFiltros().some(filtroRespuesta => filtroRespuesta.esComparativa);
    }

    public obtenerComparativo(): Nilable<FiltroRespuesta> {
        return this.obtenerFiltros().find(filtroRespuesta => filtroRespuesta.esComparativa) ?? null;
    }

    public cargarFiltroURL(filtrosURL: string, investigacion: InvestigacionDTO) {
        const filtros: FiltroRespuesta[] = JSON.parse(decodeURI(atob(filtrosURL)))
            .map((filtroRespuesta: FiltroRespuesta) => {
                const encuestaDTO = investigacion.encuestas.find(encuesta =>
                    encuesta.preguntas?.some(pregunta => pregunta.id === filtroRespuesta.pregunta.id)
                ) ?? new EncuestaDTO();

                filtroRespuesta.pregunta = encuestaDTO.preguntas?.find(pregunta =>
                    pregunta.id === filtroRespuesta.pregunta.id) ??
                    new PreguntaDTO({ text: filtroRespuesta.pregunta.texto });
                filtroRespuesta.item = filtroRespuesta.pregunta.items.find(item =>
                    item.id === filtroRespuesta.item.id
                ) ?? new ItemPreguntaDTO({ text: filtroRespuesta.item.texto });

                return filtroRespuesta;
            });
        this._filtros$.next([...filtros]);
    }

    private sacarComparativoViejo(): FiltroRespuesta[] {
        let filtros = this.obtenerFiltros();
        const filtroComparativo = filtros.find(filtroRespuesta => filtroRespuesta.esComparativa);

        if (filtroComparativo != null) {
            const indexFiltro = filtros.indexOf(filtroComparativo);
            filtros = filtros.splice(indexFiltro, 1);
        }
        return filtros;
    }

    private guardarFiltrosUrl(filtros: FiltroRespuesta[]) {
        const actualPath = this.location.path();
        const actualPathSplit = actualPath.split("/");
        const filtrosString = btoa(encodeURI(JSON.stringify(this.simplificarFiltrosURL(filtros))));

        if (filtros.length !== 0) {
            if (actualPathSplit[actualPathSplit.length - 2].includes("filtro")) {
                actualPathSplit[actualPathSplit.length - 1] = filtrosString;
            } else {
                actualPathSplit.push("filtro", filtrosString);
            }
        } else {
            if (actualPathSplit[actualPathSplit.length - 2].includes("filtro")) {
                actualPathSplit.splice(actualPathSplit.length - 2, 2);
            }
        }

        this.location.replaceState(actualPathSplit.join("/"));
    }

    private borrarFiltrosURL() {
        const actualPath = this.location.path();
        const actualPathSplit = actualPath.split("/");
        if (actualPathSplit[actualPathSplit.length - 1].includes("filtro")) {
            actualPathSplit.splice(actualPathSplit.length - 1, 1);
            return;
        }
        actualPathSplit.splice(actualPathSplit.length - 2, 2);
        this.location.replaceState(actualPathSplit.join("/"));
    }

    private simplificarFiltrosURL(filtros: FiltroRespuesta[]): FiltroRespuesta[] {
        return filtros.map(filtro => ({
            esComparativa: filtro.esComparativa,
            pregunta: {
                id: filtro.pregunta.id,
                texto: filtro.pregunta.texto
            } as PreguntaDTO,
            item: {
                id: filtro.item.id,
                texto: filtro.item.texto
            } as ItemPreguntaDTO
        }));
    }
}
